import React, { memo, useCallback, useEffect, useState } from 'react';
import { AuthLayout } from 'layouts/AuthLayout';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Spinner } from 'reactstrap';
import Api from 'services/api';

const Footer = memo(() => (
    <div className='center_wrapper'>
        <p className="para3">Already verified? <Link to="/login">Sign in here</Link></p>
    </div>
));

export default function VerifyEmailPending() {
    const location = useLocation();
    const passedEmail = location?.state?.email || '';

    const [email, setEmail] = useState(passedEmail);
    const [sentToEmail, setSentToEmail] = useState('');
    const [showSentCard, setShowSentCard] = useState(false);
    const [loading, setLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    const handleResend = useCallback(async (e) => {
        if (e) e.preventDefault();
        if (!email) {
            toast.error("Please enter your email address.");
            return;
        }

        setLoading(true);
        try {
            const response = await Api.resendVerification({ email });
            if (response?.data?.success) {
                toast.success(response?.data?.message || "Verification link sent! Please check your email inbox.");
                setSentToEmail(email);
                setShowSentCard(true);
                setCountdown(60); // 60s cooldown
            } else {
                toast.error(response?.data?.message || "Failed to resend verification email. Please try again.");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || "Something went wrong while sending verification email.");
        } finally {
            setLoading(false);
        }
    }, [email]);

    return (
        <AuthLayout
            heading="Email Verification Required"
            title="Your agency vendor account is awaiting email verification. Please verify your email before logging in."
            footer={<Footer />}
        >
            <div className='auth_form'>
                {showSentCard && (
                    <div style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #bce1e8',
                        borderRadius: '10px',
                        padding: '16px 20px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{
                            width: '42px',
                            height: '42px',
                            borderRadius: '50%',
                            backgroundColor: '#015969',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <i className="fa-solid fa-envelope-circle-check" style={{ color: '#fff', fontSize: '18px' }}></i>
                        </div>
                        <div>
                            <div style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>Verification sent to:</div>
                            <div style={{ fontSize: '15px', color: '#015969', fontWeight: '700', wordBreak: 'break-all' }}>
                                {sentToEmail}
                            </div>
                        </div>
                    </div>
                )}

                <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)', lineHeight: '1.6', marginBottom: '20px' }}>
                    We have sent a verification link to your email address. Please open the email and click the verification button to activate your account.
                </p>

                <div className="form-group mb-3">
                    <label style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.95)', marginBottom: '6px', display: 'block' }}>
                        Email Address
                    </label>
                    <div className="row-input">
                        <img src="/images/mail.png" alt="email" />
                        <input
                            type="email"
                            name="email"
                            id="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your registered email"
                        />
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <button
                        type="button"
                        onClick={handleResend}
                        disabled={loading || countdown > 0}
                        className={`btn_auth ${loading || countdown > 0 ? "disabled-button" : ""}`}
                        style={{ cursor: countdown > 0 ? 'not-allowed' : 'pointer' }}
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                <span>Sending...</span>
                            </>
                        ) : countdown > 0 ? (
                            <span>Resend available in {countdown}s</span>
                        ) : (
                            <span>
                                <i className="fa-solid fa-paper-plane me-2"></i>
                                Resend Verification Email
                            </span>
                        )}
                    </button>

                    <div style={{ textAlign: 'center', marginTop: '10px' }}>
                        <Link to="/login" style={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: '600', textDecoration: 'none', fontSize: '14px' }}>
                            <i className="fa-solid fa-arrow-left me-2"></i>
                            Back to Login
                        </Link>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
}
